import React, { useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { BeamTrussArrow } from '../../types/beamTruss';

interface SlantedPressureArrowProps {
  position: THREE.Vector3;
  length: number;
  color: string;
  direction: [number, number, number];
  side: 'left' | 'right';
  scaleFactor?: number;
}

interface DualPressureIndicatorProps {
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  leftPressureValue: number;
  rightPressureValue: number;
  baseArrowLength: number;
  visible?: boolean;
  scaleFactor?: number;
}

// Individual slanted pressure arrow component
const SlantedPressureArrow: React.FC<SlantedPressureArrowProps> = ({ 
  position, 
  length, 
  color, 
  direction,
  scaleFactor = 1
}) => {
  // Get load visual scale from pre-calculated fixed values
  // scaleFactor now represents scaleRange from useUIStore
  const visualScale = getLoadVisualScale(scaleFactor);
  
  const { arrowLine, arrowHead } = useMemo(() => {
    const dir = new THREE.Vector3(...direction).normalize();
    const arrowStart = position.clone();
    const arrowEnd = position.clone().add(dir.clone().multiplyScalar(length));
    
    // Create arrowhead (small cone at the end pointing toward beam) - scaled based on model scale
    const coneRadius = 0.015 * visualScale;
    const coneHeight = 0.04 * visualScale;
    const arrowHeadGeometry = new THREE.ConeGeometry(coneRadius, coneHeight, 8);
    
    // Z-UP: Rotate cone to point in the specified direction (toward the beam)
    const rotationMatrix = new THREE.Matrix4();
    const up = new THREE.Vector3(0, 0, 1);  // Z-UP convention
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(up, dir);
    rotationMatrix.makeRotationFromQuaternion(quaternion);
    arrowHeadGeometry.applyMatrix4(rotationMatrix);
    
    return {
      arrowLine: [arrowStart, arrowEnd],
      arrowHead: { geometry: arrowHeadGeometry, position: arrowEnd }
    };
  }, [position, length, direction, visualScale]);

  return (
    <group>
      {/* Arrow line - pointing toward beam */}
      <Line
        points={arrowLine}
        color={color}
        lineWidth={2}
      />
      {/* Arrow head */}
      <mesh position={arrowHead.position} geometry={arrowHead.geometry}>
        <meshStandardMaterial color={color} />
      </mesh>
      {/* Arrow head outline */}
      <mesh position={arrowHead.position} geometry={arrowHead.geometry} scale={[1.1, 1.1, 1.1]}>
        <meshBasicMaterial color="black" wireframe transparent opacity={0.6} />
      </mesh>
    </group>
  );
};

/**
 * Draw slanted pressure load on beam side
 * @param beamId - ID of the beam
 * @param side - Which side ('left' or 'right')
 * @param magnitude - Pressure magnitude value
 */
const drawSlantedPressure = (
  startPoint: THREE.Vector3,
  endPoint: THREE.Vector3,
  side: 'left' | 'right',
  magnitude: number,
  baseArrowLength: number
) => {
  const beamVector = endPoint.clone().sub(startPoint);
  const beamDirection = beamVector.clone().normalize();
  
  // Calculate arrow length based on pressure magnitude
  const arrowLength = magnitude > 0 ? baseArrowLength * Math.log(magnitude + 1) : baseArrowLength;
  
  // Create slanted line that stays within beam boundaries
  const nodeRadius = 0.05;
  const adjustedStartPoint = startPoint.clone().add(beamDirection.clone().multiplyScalar(nodeRadius));
  const adjustedEndPoint = endPoint.clone().sub(beamDirection.clone().multiplyScalar(nodeRadius));
  
  // Z-UP: Calculate upward vector for beam's top edge alignment
  const upVector = new THREE.Vector3(0, 0, 1);  // Z-UP convention
  const beamRadius = 0.1; // Match BeamManager.BEAM_RADIUS
  
  // Calculate beam's top edge points (pressure diagrams should be above the beam)
  const beamTopStart = adjustedStartPoint.clone().add(upVector.clone().multiplyScalar(beamRadius));
  const beamTopEnd = adjustedEndPoint.clone().add(upVector.clone().multiplyScalar(beamRadius));
  
  // Calculate the slant direction - create a triangular load diagram
  const slantHeight = arrowLength * 2; // Height of the slanted pressure diagram
  
  let slantStartPoint: THREE.Vector3;
  let slantEndPoint: THREE.Vector3;
  let beamConnectionStart: THREE.Vector3;
  let beamConnectionEnd: THREE.Vector3;
  
  if (side === 'left') {
    // Left pressure: start high above left end, slant down toward right end at beam top
    beamConnectionStart = beamTopStart.clone();
    beamConnectionEnd = beamTopEnd.clone();
    
    slantStartPoint = beamConnectionStart.clone().add(new THREE.Vector3(0, slantHeight, 0));
    slantEndPoint = beamConnectionEnd.clone(); // Connect to beam top edge
  } else {
    // Right pressure: start high above right end, slant down toward left end at beam top
    // This mirrors the left pressure but in opposite direction
    beamConnectionStart = beamTopEnd.clone();
    beamConnectionEnd = beamTopStart.clone();
    
    slantStartPoint = beamConnectionStart.clone().add(new THREE.Vector3(0, slantHeight, 0));
    slantEndPoint = beamConnectionEnd.clone(); // Connect to beam top edge
  }
  
  return {
    slantLine: [slantStartPoint, slantEndPoint],
    arrowLength,
    slantStartPoint,
    slantEndPoint,
    beamStartPoint: beamConnectionStart,
    beamEndPoint: beamConnectionEnd
  };
};

/**
 * Connect truss to beam from specific side
 * @param beamId - ID of the beam
 * @param side - Which side to connect from
 */
const connectTrussToBeam = (
  startPoint: THREE.Vector3,
  endPoint: THREE.Vector3,
  side: 'left' | 'right'
) => {
  const beamVector = endPoint.clone().sub(startPoint);
  const beamDirection = beamVector.clone().normalize();
  const nodeRadius = 0.05;
  
  // Connection point is at the appropriate end of the beam
  if (side === 'left') {
    return startPoint.clone().add(beamDirection.clone().multiplyScalar(nodeRadius));
  } else {
    return endPoint.clone().sub(beamDirection.clone().multiplyScalar(nodeRadius));
  }
};

/**
 * Update pressure visual for beam
 * @param beamId - ID of the beam to update
 */
const updatePressureVisual = (beamId: string) => {
  // This function would trigger a re-render of the pressure visualization
  console.log(`Updating pressure visual for beam: ${beamId}`);
};

// Main dual pressure indicator component
export const DualPressureIndicator: React.FC<DualPressureIndicatorProps> = ({
  startPoint,
  endPoint,
  leftPressureValue,
  rightPressureValue,
  baseArrowLength,
  visible = true,
  scaleFactor = 1
}) => {
  // Get load visual scale from pre-calculated fixed values
  // scaleFactor now represents scaleRange from useUIStore
  const visualScale = getLoadVisualScale(scaleFactor);
  
  const { leftPressureData, rightPressureData } = useMemo(() => {
    if (!visible) return { leftPressureData: null, rightPressureData: null };

    const beamVector = endPoint.clone().sub(startPoint);
    const beamLength = beamVector.length();
    
    if (beamLength === 0) return { leftPressureData: null, rightPressureData: null };

    let leftPressureData = null;
    let rightPressureData = null;

    // Generate left pressure visualization if value > 0
    if (leftPressureValue > 0) {
      const leftData = drawSlantedPressure(startPoint, endPoint, 'left', leftPressureValue, baseArrowLength);
      
      // Generate arrows with proper spacing to avoid "black wall" effect
      // Use a reasonable number of arrows based on beam length with minimum spacing
      const minArrowSpacing = 0.2 * visualScale; // Scaled minimum distance between arrows
      const maxArrows = Math.floor(beamLength / minArrowSpacing);
      const arrowCount = Math.min(Math.max(3, maxArrows), 12); // Between 3-12 arrows
      const arrows: BeamTrussArrow[] = [];
      
      for (let i = 0; i <= arrowCount; i++) {
        const t = i / arrowCount;
        
        // Position along the beam's bottom edge (interpolate between beam start and end points)
        const beamPosition = leftData.beamStartPoint.clone().lerp(leftData.beamEndPoint, t);
        
        // Position on the slanted line (interpolate between slant start and end)
        const slantPosition = leftData.slantStartPoint.clone().lerp(leftData.slantEndPoint, t);
        
        // Arrow starts from slant line and points toward beam's bottom edge
        const arrowDirection = beamPosition.clone().sub(slantPosition).normalize();
        const arrowLength = slantPosition.distanceTo(beamPosition);
        
        arrows.push({
          position: {
            x: slantPosition.x,
            y: slantPosition.y,
            z: slantPosition.z
          },
          size: arrowLength,
          direction: [arrowDirection.x, arrowDirection.y, arrowDirection.z]
        });
      }
      
      leftPressureData = {
        slantLine: leftData.slantLine,
        beamLine: [leftData.beamStartPoint, leftData.beamEndPoint],
        arrows
      };
    }

    // Generate right pressure visualization if value > 0
    if (rightPressureValue > 0) {
      const rightData = drawSlantedPressure(startPoint, endPoint, 'right', rightPressureValue, baseArrowLength);
      
      // Generate arrows with proper spacing to avoid "black wall" effect
      // Use a reasonable number of arrows based on beam length with minimum spacing
      const minArrowSpacing = 0.2 * visualScale; // Scaled minimum distance between arrows
      const maxArrows = Math.floor(beamLength / minArrowSpacing);
      const arrowCount = Math.min(Math.max(3, maxArrows), 12); // Between 3-12 arrows
      const arrows: BeamTrussArrow[] = [];
      
      for (let i = 0; i <= arrowCount; i++) {
        const t = i / arrowCount;
        
        // Position along the beam's bottom edge (interpolate between beam start and end points)
        const beamPosition = rightData.beamStartPoint.clone().lerp(rightData.beamEndPoint, t);
        
        // Position on the slanted line (interpolate between slant start and end)
        const slantPosition = rightData.slantStartPoint.clone().lerp(rightData.slantEndPoint, t);
        
        // Arrow starts from slant line and points toward beam's bottom edge
        const arrowDirection = beamPosition.clone().sub(slantPosition).normalize();
        const arrowLength = slantPosition.distanceTo(beamPosition);
        
        arrows.push({
          position: {
            x: slantPosition.x,
            y: slantPosition.y,
            z: slantPosition.z
          },
          size: arrowLength,
          direction: [arrowDirection.x, arrowDirection.y, arrowDirection.z]
        });
      }
      
      rightPressureData = {
        slantLine: rightData.slantLine,
        beamLine: [rightData.beamStartPoint, rightData.beamEndPoint],
        arrows
      };
    }
    
    return { leftPressureData, rightPressureData };
  }, [startPoint, endPoint, leftPressureValue, rightPressureValue, baseArrowLength, visible, visualScale]);

  if (!visible) return null;

  const blackColor = '#000000'; // All elements are black as requested

  return (
    <group>
      {/* Left side slanted pressure */}
      {leftPressureData && (
        <group>
          {/* Main slanted line */}
          <Line
            points={leftPressureData.slantLine}
            color={blackColor}
            lineWidth={2}
          />
          {/* Connection lines from slant to beam ends */}
          <Line
            points={[leftPressureData.slantLine[0], leftPressureData.beamLine[0]]}
            color={blackColor}
            lineWidth={2}
          />
          <Line
            points={[leftPressureData.slantLine[1], leftPressureData.beamLine[1]]}
            color={blackColor}
            lineWidth={2}
          />
          {/* Pressure arrows pointing toward the beam */}
          {leftPressureData.arrows.map((arrow, index) => (
            <SlantedPressureArrow
              key={`left-${index}`}
              position={new THREE.Vector3(arrow.position.x, arrow.position.y, arrow.position.z)}
              length={arrow.size}
              color={blackColor}
              direction={arrow.direction}
              side="left"
              scaleFactor={scaleFactor}
            />
          ))}
        </group>
      )}
      
      {/* Right side slanted pressure */}
      {rightPressureData && (
        <group>
          {/* Main slanted line */}
          <Line
            points={rightPressureData.slantLine}
            color={blackColor}
            lineWidth={2}
          />
          {/* Connection lines from slant to beam ends */}
          <Line
            points={[rightPressureData.slantLine[0], rightPressureData.beamLine[0]]}
            color={blackColor}
            lineWidth={2}
          />
          <Line
            points={[rightPressureData.slantLine[1], rightPressureData.beamLine[1]]}
            color={blackColor}
            lineWidth={2}
          />
          {/* Pressure arrows pointing toward the beam */}
          {rightPressureData.arrows.map((arrow, index) => (
            <SlantedPressureArrow
              key={`right-${index}`}
              position={new THREE.Vector3(arrow.position.x, arrow.position.y, arrow.position.z)}
              length={arrow.size}
              color={blackColor}
              direction={arrow.direction}
              side="right"
              scaleFactor={scaleFactor}
            />
          ))}
        </group>
      )}
    </group>
  );
};

// Export the utility functions
export { drawSlantedPressure, connectTrussToBeam, updatePressureVisual };
